import { useEffect } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";

import { extractionResult, formChoiceOf, NO_EXTRACTION, ProblemError, reviewOf, useExtraction } from "@/api";
import { StateMessage } from "@/components/StateMessage";
import { useDemoRun } from "@/store/demoRun";

import { CaseFormChoice, CaseReview, OTHER_READING_PARAM } from "./review/CaseReview";
import { ReviewSkeleton } from "./review/ReviewSkeleton";
import { SchemaScreen } from "./SchemaScreen";

/**
 * The one sentence a person gets here (PF-5). `ProblemError`'s own
 * `.message` is the problem's **title**, not the server's actual sentence —
 * `.userMessage` is what has to be shown; same pattern as
 * `features/reading/ReadingJobScreen.tsx` and `components/DropArea.tsx`.
 */
function messageOf(cause: unknown): string {
  return cause instanceof ProblemError
    ? cause.userMessage
    : cause instanceof Error
      ? cause.message
      : String(cause);
}

/**
 * `/vorgang/:caseId/schema` — the worker's reading of this Vorgang (design
 * 2026-09-23 §3.6; plan 4 §2).
 *
 * A v2 reading (`lesekette-4`) carries a `review` and is shown as the review
 * screen; a v1 reading keeps the schema view it always had. Which one it is
 * only the status answer says, so it is always asked — straight from the
 * progress page the answer is already in the query cache (same key), after
 * a reload it is fetched. A v1 reading already in the store is shown at once
 * while that answer arrives. A reading still in work is not this route's
 * business; it sends the visitor back to where it *is* being shown.
 */
export function CaseSchemaRoute() {
  const { caseId = "" } = useParams();
  const current = useDemoRun((state) => state.caseId);
  const fromExtraction = useDemoRun((state) => state.fromExtraction);
  const adoptExtraction = useDemoRun((state) => state.adoptExtraction);
  const inHand = fromExtraction && current === caseId;
  const [params] = useSearchParams();
  const job = useExtraction(caseId || null);
  const review = job.data ? reviewOf(job.data) : null;
  const choice = formChoiceOf(job.data);

  useEffect(() => {
    if (inHand || !job.data || reviewOf(job.data) !== null) return;
    const result = extractionResult(job.data);
    if (result !== null) adoptExtraction(caseId, result);
  }, [inHand, job.data, caseId, adoptExtraction]);

  if (job.data && job.data.status === "succeeded" && review !== null) {
    return <CaseReview caseId={caseId} review={review} source={job.data.source} onReload={() => void job.refetch()} />;
  }

  // The measures of a chosen reading failed (review I-1): „Andere Lesart“
  // from the failure offers the readings of the form the server names on
  // the failed job — in this tab or any other.
  if (job.data?.status === "failed" && choice !== null && params.get(OTHER_READING_PARAM) === "1") {
    return <CaseFormChoice caseId={caseId} choice={choice} onReload={() => void job.refetch()} />;
  }

  // The old schema view only ever for a v1 reading (review I-1): one in
  // hand while the answer is on its way, or an answer that carries a view
  // and no review. A job in work or failed goes to its progress below —
  // whatever the store holds; the old view's take-over would write READ
  // walls without the re-check.
  if (inHand && (job.data === undefined || (review === null && extractionResult(job.data) !== null))) {
    return <SchemaScreen />;
  }

  // No reading at all: where one can be started (Kauf-Test 11.10.2026) —
  // the reading screen offers the drawings on file, or says there are none.
  if (job.isError && job.error.message === NO_EXTRACTION) {
    return <Navigate to={`/vorgang/${caseId}/einlesen`} replace />;
  }

  if (job.isError) {
    // Title and body must not say the same thing twice (Ruling T7-1): the
    // title names what's wrong with this *route*, the body carries the
    // server's own sentence, which may already name what is missing.
    return (
      <StateMessage
        tone="conflict"
        title="Die Lesung lässt sich nicht laden."
        // For *this* Vorgang (UX gate round 1, R-4): the free `/umriss`
        // would open a second one beside it.
        action={<Link className="inline-link" to={`/umriss?case=${encodeURIComponent(caseId)}`}>Umriss selbst zeichnen</Link>}
      >
        {messageOf(job.error)}
      </StateMessage>
    );
  }

  if (job.data && job.data.status !== "succeeded") {
    return <Navigate to={`/vorgang/${caseId}/einlesen`} replace />;
  }
  if (job.data && extractionResult(job.data) === null) {
    return <Navigate to={`/vorgang/${caseId}/einlesen`} replace />;
  }

  // The answer is on its way: the review's geometry under the frame that
  // already names the Vorgang — never a loading line (Task 10, B-4).
  return <ReviewSkeleton />;
}
